<script setup lang="ts">
import { useBillingUtils } from '@/use/billingUtils';
import { usePauseAvailability } from '@/use/pauseAvailability';
import { useToast } from '@/use/toast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { refreshCurrentUser } from '@/lib/refreshUser';
import { useBillingStore } from '@/stores/billing';
import { useUserStore } from '@/stores/user';
import type { Offerings, PlanProduct, Subscription } from '@/types/billing';
import {
  Block,
  Button,
  Dropdown,
  Heading,
  Icon,
  Menu,
  MenuItem,
  Modal,
  Prose,
  useConfirmStore,
  vTooltip,
} from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed, onMounted, ref } from 'vue';
import AppSkeleton from '@/components/AppSkeleton/AppSkeleton.vue';
import { useRoute, useRouter } from 'vue-router';
import BillingLifetimeWelcome from '@/components/BillingLifetimeWelcome/BillingLifetimeWelcome.vue';
import BillingPlanSeal from '@/components/BillingPlanSeal/BillingPlanSeal.vue';
import { accentForPlanName, type SealMetal } from '@/components/BillingPlanSeal/seal';
import { consumeCheckoutCelebration } from '@/lib/celebrateCheckout';
import BillingCouponApplyButton from '@/components/BillingCouponApplyButton/BillingCouponApplyButton.vue';
import BillingDiscountAlert from '@/components/BillingDiscountAlert/BillingDiscountAlert.vue';
import BillingPaymentMethodList from '@/components/BillingPaymentMethodList/BillingPaymentMethodList.vue';
import BillingCancelSubscriptionForm from '@/components/BillingCancelSubscriptionForm/BillingCancelSubscriptionForm.vue';
import BillingInvoiceList from '@/components/BillingInvoiceList/BillingInvoiceList.vue';
import BillingPauseSubscriptionForm from '@/components/BillingPauseSubscriptionForm/BillingPauseSubscriptionForm.vue';
import BillingSubscriptionStatusBadge from '@/components/BillingSubscriptionStatusBadge/BillingSubscriptionStatusBadge.vue';
import BillingSubscriptionAlert from '@/components/BillingSubscriptionAlert/BillingSubscriptionAlert.vue';
import BillingUpcomingInvoiceSummary from '@/components/BillingUpcomingInvoiceSummary/BillingUpcomingInvoiceSummary.vue';

const { formatPrice, formatUnixDate, trialCancelConfirmBody } = useBillingUtils();
const { showToast } = useToast();
const router = useRouter();
const route = useRoute();
const billingStore = useBillingStore();
const userStore = useUserStore();
const confirmStore = useConfirmStore();
const { t } = useI18n();

const actionDisabled = ref(false);
const showPauseModal = ref(false);
const showCancelModal = ref(false);

/** True until initial user + billing fetch completes — avoids flashing "no plan" before we know state. */
const portalHydrating = ref(true);

const subscription = computed(() => billingStore.primarySubscription);
const plan = computed(() => subscription.value?.plan);

const isLifetime = computed(() => billingStore.isLifetime);
const lifetimeUpgradeName = computed(() =>
  billingStore.canUpgradeLifetime ? (billingStore.lifetimeUpgrade?.product?.name ?? '') : '',
);
const currentProductId = computed(() => plan.value?.product?.id);

const planName = computed(() => {
  const name = plan.value?.name || plan.value?.product?.name || '';
  return isLifetime.value && name ? t('billing_plan_name_lifetime', { name }) : name;
});

/** The plan's tier name without the "Lifetime" suffix, for the seal engraving + perks. */
const baseName = computed(() => plan.value?.name || plan.value?.product?.name || '');

// Bronze for monthly, silver for annual, gold for lifetime. Trials show the
// metal of the plan they're trialing — the status badge already says it's a
// trial, and the seal is the thing worth subscribing to keep.
const sealMetal = computed<SealMetal | null>(() => {
  if (!subscription.value || !plan.value) return null;
  if (isLifetime.value) return 'gold';
  return isYearlyPlan.value ? 'silver' : 'bronze';
});

const sealText = computed(() =>
  sealMetal.value === 'gold' ? t('billing_seal_lifetime') : t('billing_seal_member', { name: baseName.value }),
);

const sealAccent = computed(() => (sealMetal.value === 'gold' ? null : accentForPlanName(baseName.value)));

const lifetimePerks = computed(() => [
  t('billing_lifetime_perk_features', { name: baseName.value }),
  t('billing_lifetime_perk_updates'),
  t('billing_lifetime_perk_payments'),
]);

const memberSince = computed(() => {
  const created = (billingStore.customer as Stripe.Customer | null)?.created;
  return formatUnixDate(created);
});

/** The lifetime $0-invoice explainer, shown under Invoice History (currency-aware). */
const lifetimeInvoiceNote = computed(() =>
  t('billing_lifetime_invoice_note', { amount: formatPrice(0, billingStore.subscriptionCurrency ?? 'usd', true) }),
);

/**
 * The currency to quote the plan in: what Stripe says the subscription bills in. Display only — unlike
 * the store's `subscriptionCurrency`, which checkout sends, this needn't wait for dabble-rest (DAB-1996).
 */
const planCurrency = computed(() => subscription.value?.currency ?? plan.value?.currency ?? 'usd');

/**
 * What the plan costs in `planCurrency`. `plan.amount` is the price's base (USD) amount, so it only
 * counts when the subscription bills in that same currency; otherwise only dabble-rest's `plan_amount`
 * will do. Null rather than a wrong number (DAB-1996: A$396 shown as $276).
 */
const billedPlanAmount = computed(() => {
  const sub = subscription.value;
  if (sub?.plan_amount != null) return sub.plan_amount;
  return plan.value?.amount != null && plan.value.currency === planCurrency.value ? plan.value.amount : null;
});

/** The mockup's one-liner for paying plans: "You're on Author, billed monthly — $29.00 a month. …" */
const planSummary = computed(() => {
  if (!plan.value || isLifetime.value) return '';
  if (billedPlanAmount.value == null) {
    const noPriceKey = isYearlyPlan.value
      ? 'billing_plan_summary_annual_noprice'
      : 'billing_plan_summary_monthly_noprice';
    return t(noPriceKey, { name: baseName.value });
  }
  const key = isYearlyPlan.value ? 'billing_plan_summary_annual' : 'billing_plan_summary_monthly';
  const price = formatPrice(billedPlanAmount.value, planCurrency.value, true);
  return t(key, { name: baseName.value, price });
});

const showWelcome = ref(false);
const welcomeUpgrade = ref(false);

/** Triggerless coupon modal host — the ellipsis menu's Apply Coupon item opens it. */
const couponButton = ref<InstanceType<typeof BillingCouponApplyButton> | null>(null);

const isPausedSubscription = computed(() => billingStore.isPausedSubscription);
const isPauseLocked = computed(() => billingStore.isPauseLocked);
const pauseLockedUntilFormatted = computed(() => formatUnixDate(billingStore.pauseLockedUntil));

// One message for the Pause item's tooltip and its click toast; the composable owns the
// cooldown-over-yearly precedence shared with the cancel survey.
const {
  reason: pauseUnavailableReason,
  cooldownDateFormatted: pauseCooldownUntilFormatted,
  isYearlyPlan,
} = usePauseAvailability();
const pauseUnavailableMessage = computed(() => {
  const reason = pauseUnavailableReason.value;
  if (!reason) return null;
  // TODO(copy): yearly + cooldown overpromises — "pause again on {date}" still needs a
  // switch to monthly first for a yearly subscriber (DAB-1232).
  const messages: Record<typeof reason, string> = {
    pendingCancel: t('billing_pause_pending_cancel_tooltip'),
    cooldown: t('billing_pause_cooldown_tooltip', { date: pauseCooldownUntilFormatted.value }),
    yearly: t('billing_pause_yearly_tooltip'),
  };
  return messages[reason];
});
const isTrialing = computed(() => billingStore.isTrialing);
const isActivePaid = computed(() => billingStore.isActivePaid);
const isSubscribedDuringTrial = computed(() => billingStore.isSubscribedDuringTrial);
const hasStripeCustomer = computed(() => billingStore.hasStripeCustomer);
// Offer "Apply coupon" whenever a subscription exists to attach it to (any
// non-terminal sub — trial, active, past-due, paused). Only a customer with no
// subscription at all applies at checkout instead (DAB-848).
const canApplyCouponToSubscription = computed(() => billingStore.canApplyCouponToSubscription);

/** Stripe customer exists but no active/trialing subscription row (or only canceled). */
const trialAvailable = computed(() => !subscription.value && billingStore.hasTrialAvailable);

function goToPlans() {
  router.push({ name: 'billing-plans' });
}

function goToExitSurvey() {
  showCancelModal.value = true;
}

function closePauseModal() {
  showPauseModal.value = false;
}

// The item stays enabled when pausing is unavailable (yearly or cooldown): a tooltip on a
// disabled item never reaches touch or keyboard users, so the click explains instead.
function onPauseClick() {
  if (pauseUnavailableReason.value) {
    showToast('info', pauseUnavailableMessage.value!);
  } else {
    showPauseModal.value = true;
  }
}

async function onStartTrialClick() {
  actionDisabled.value = true;
  try {
    if (!hasStripeCustomer.value) {
      const stripeId = await billingStore.createCustomer({
        uid: userStore.user.uid ?? '',
        email: userStore.user.email ?? '',
        name: userStore.user.name ?? '',
        withTrial: true,
      });
      if (!stripeId) return;
      await billingStore.fetchBillingOverview();
      return;
    }
    await billingStore.startTrialSubscription();
  } finally {
    actionDisabled.value = false;
  }
}

async function onSubscribeNowEnsureCustomer() {
  if (!hasStripeCustomer.value) {
    const stripeId = await billingStore.createStripeCustomerOnly({
      uid: userStore.user.uid ?? '',
      email: userStore.user.email ?? '',
      name: userStore.user.name ?? '',
    });
    if (!stripeId) return;
  }
  goToPlans();
}

// The product entry for the current plan within a returned offering: by product-id
// key, else by matching the current price id (item/price-based subs carry a price id).
function findCurrentProduct(offering: Offerings | null, currentPlan: Subscription['plan']): PlanProduct | undefined {
  const products = offering?.products;
  if (!products || !currentPlan) return undefined;
  const byProductId = currentPlan.product?.id ? products[currentPlan.product.id] : undefined;
  if (byProductId) return byProductId;
  const priceId = currentPlan.id;
  return Object.values(products).find(
    (p) => p.month?.id === priceId || p.year?.id === priceId || p['one-time']?.id === priceId,
  );
}

async function switchToAnnual() {
  actionDisabled.value = true;
  showToast('info', t('billing_finding_annual'));
  try {
    // Resolve the plan's own offering, so an aged-out product still finds its annual.
    const priceId = plan.value?.id;
    const offering = priceId ? await billingStore.fetchOfferingForPrice(priceId) : null;
    const currentProduct = findCurrentProduct(offering, plan.value);
    const annualPlan = currentProduct?.year;

    if (currentProduct && annualPlan?.id) {
      billingStore.product = currentProduct;
      billingStore.selectedPlan = annualPlan;
      billingStore.selectedPlanId = annualPlan.id;
      billingStore.selection = 'year';
      router.push('/billing/checkout');
    } else {
      // The current product legitimately has no annual price (an aged-out or
      // annual-less plan). This is an expected outcome, not a first-party bug:
      // tell the user, but don't report it to Sentry as an error
      // (DABBLE-ACCOUNTS-2-1S). A genuine failure — the offering fetch throwing —
      // is still caught and reported below.
      showToast('danger', t('billing_annual_not_found'));
    }
  } catch (error: unknown) {
    logError('Failed to switch to annual:', error);
    showToast('danger', t('billing_annual_not_found'));
  } finally {
    actionDisabled.value = false;
  }
}

function upgradeLifetime() {
  if (billingStore.selectLifetimeUpgrade()) router.push('/billing/checkout');
}

async function confirmAndCancelSubscription(when: 'now' | 'end_of_period') {
  const isTrial = billingStore.isTrialing;
  const titleKey = isTrial ? 'billing_cancel_confirm_trial_title' : 'billing_cancel_confirm_subscription_title';
  const cancelKey = isTrial ? 'billing_cancel_trial' : 'billing_cancel_subscription';
  // A trial cancel is immediate and forfeits the remaining days — spell that out (DAB-922).
  const body = isTrial
    ? trialCancelConfirmBody(subscription.value?.trial_end)
    : t('billing_cancel_confirm_subscription_body');

  const confirmed = await confirmStore.confirm(t(titleKey), body, {
    cancel: { text: t('billing_keep_it') },
    confirm: { text: t(cancelKey), variant: 'danger' },
  });

  if (!confirmed) return;

  actionDisabled.value = true;
  try {
    const ok = await billingStore.cancelSubscription({
      when: when === 'now' ? 'now' : undefined,
    });
    if (ok) {
      showToast('success', t('billing_cancel_success'));
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Could not cancel subscription.';
    logError('Failed to cancel subscription:', error);
    showToast('danger', message);
  } finally {
    actionDisabled.value = false;
  }
}

async function unpauseFromPortal() {
  actionDisabled.value = true;
  try {
    const ok = await billingStore.unpauseBillingSubscription();
    if (ok) {
      showToast('success', t('billing_resumed'));
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Could not resume subscription.';
    logError('Failed to unpause subscription:', error);
    showToast('danger', message);
  } finally {
    actionDisabled.value = false;
  }
}

async function undoCancellation() {
  actionDisabled.value = true;
  try {
    const ok = await billingStore.resumeSubscription();
    if (ok) showToast('success', t('billing_undo_cancel_success'));
  } finally {
    actionDisabled.value = false;
  }
}

function handleSubscriptionPaused() {
  showPauseModal.value = false;
}

onMounted(async () => {
  // Back-compat: old `?changePlan=1` links now open the full-screen plans step.
  if (route.query.changePlan) {
    await router.replace({ name: 'billing-plans' });
    // A lifetime holder gets bounced straight back here by the plans-route guard —
    // same route record, so vue-router reuses this component instance and onMounted
    // won't run again. Fall through into hydration instead of leaving
    // `portalHydrating` stuck on the skeleton forever. Anything else means the
    // replace actually left the portal, so there's nothing left for this mount to do.
    if (route.name !== 'portal') return;
  }
  try {
    if (userStore.user.uid) {
      // An expired session surfaces here as a 401 and redirects to login; skip
      // the billing fetch when that happens (it would only 401 again).
      const refreshed = await refreshCurrentUser(userStore.user.uid);
      if (refreshed) {
        await billingStore.fetchBillingOverview();
        // Existing customers already got `hasTrialAvailable` from `/billing/data`.
        // Only the no-customer case needs the customer-optional `/billing/trial`
        // (which `/billing/data` can't answer), so skip the extra Stripe
        // customer-search + subscription-list round-trip when a customer exists.
        if (!hasStripeCustomer.value) {
          await billingStore.fetchTrialStatus();
        }
      }
    }
  } finally {
    portalHydrating.value = false;
  }

  // Checkout drops a one-shot flag on its way here; a completed lifetime
  // purchase gets the full welcome moment (Jacob's "welcome to Dabble for
  // life"), and a lifetime upgrade its own take on it. Confetti for month/year
  // upgrades can hang off the same flag later.
  const celebration = consumeCheckoutCelebration();
  if ((celebration === 'lifetime' || celebration === 'upgrade') && isLifetime.value) {
    welcomeUpgrade.value = celebration === 'upgrade';
    showWelcome.value = true;
  }
});
</script>

<template>
  <div class="billing-portal">
    <header class="billing-portal__header">
      <Heading :level="1" family="secondary" :margin="false" class="billing-portal__title">{{
        t('billing_title')
      }}</Heading>
      <Prose variant="secondary" class="billing-portal__lede">{{ t('billing_portal_lede') }}</Prose>
    </header>

    <Block
      v-if="portalHydrating"
      variant="canvas"
      padding="large"
      elevation="small"
      border-radius="medium"
      class="billing-portal__card"
    >
      <span class="billing-portal__eyebrow">{{ t('billing_plan_current') }}</span>
      <div class="billing-portal__skeleton">
        <AppSkeleton width="9rem" height="1.5rem" />
        <AppSkeleton width="6.5rem" height="2rem" />
        <AppSkeleton width="14rem" height="0.85rem" />
      </div>
    </Block>

    <template v-else>
      <Block
        variant="canvas"
        padding="large"
        elevation="small"
        border-radius="medium"
        class="billing-portal__card"
        :class="sealMetal && `billing-portal__card--${sealMetal}`"
      >
        <div class="billing-portal__plan-row">
          <div v-if="sealMetal" class="billing-portal__seal" :class="{ 'billing-portal__seal--gold': isLifetime }">
            <BillingPlanSeal :metal="sealMetal" :top-text="sealText" :accent="sealAccent" :aria-label="sealText" />
            <template v-if="isLifetime">
              <svg
                v-for="n in 3"
                :key="n"
                class="billing-portal__spark"
                :class="`billing-portal__spark--${n}`"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 0l2.4 9.6L24 12l-9.6 2.4L12 24l-2.4-9.6L0 12l9.6-2.4z" />
              </svg>
            </template>
          </div>

          <div v-if="subscription && plan" class="billing-portal__plan">
            <span class="billing-portal__eyebrow">{{ t('billing_plan_current') }}</span>
            <div class="billing-portal__name-row">
              <Heading
                :level="3"
                :semantic-level="2"
                family="secondary"
                :margin="false"
                class="billing-portal__plan-name"
                >{{ planName }}</Heading
              >
              <BillingSubscriptionStatusBadge :subscription="subscription" />
            </div>
            <Prose v-if="plan?.description" variant="secondary" class="billing-portal__plan-desc">
              {{ plan.description }}
            </Prose>

            <template v-if="isLifetime">
              <Prose class="billing-portal__congrats">{{ t('billing_lifetime_plan') }}</Prose>
              <Prose variant="secondary" class="billing-portal__congrats-detail">
                {{ t('billing_lifetime_congrats_detail') }}
              </Prose>
              <ul class="billing-portal__perks">
                <li v-for="perk in lifetimePerks" :key="perk" class="billing-portal__perk">
                  <Icon name="check" class="billing-portal__perk-check" />
                  {{ perk }}
                </li>
              </ul>
            </template>
            <Prose v-else class="billing-portal__summary">{{ planSummary }}</Prose>

            <BillingSubscriptionAlert
              :subscription="subscription"
              :disabled="actionDisabled"
              :payment-processing="billingStore.isPaymentProcessing"
              @undo-cancellation="undoCancellation"
            />
            <BillingDiscountAlert v-if="hasStripeCustomer" />

            <div class="billing-portal__meta">
              <div v-if="memberSince" class="billing-portal__stat">
                <span class="billing-portal__stat-label">{{ t('billing_member_since') }}</span>
                <span class="billing-portal__stat-value">{{ memberSince }}</span>
              </div>
              <BillingUpcomingInvoiceSummary v-if="hasStripeCustomer" class="billing-portal__meta-summary" />

              <div class="billing-portal__meta-actions">
                <!-- Lifetime: the moment, and the one upgrade dabble-rest may offer. -->
                <template v-if="isLifetime">
                  <Button
                    v-if="lifetimeUpgradeName"
                    v-tooltip="t('billing_lifetime_upgrade_message', { name: lifetimeUpgradeName })"
                    variant="primary"
                    finish="outlined"
                    :disabled="actionDisabled"
                    @click="upgradeLifetime"
                  >
                    {{ t('billing_lifetime_upgrade', { name: lifetimeUpgradeName }) }}
                  </Button>
                  <button type="button" class="billing-portal__replay" @click="showWelcome = true">
                    {{ t('billing_lifetime_replay') }}
                  </button>
                </template>

                <!-- Paused -->
                <template v-else-if="isPausedSubscription">
                  <Button
                    variant="primary"
                    finish="outlined"
                    :disabled="actionDisabled || isPauseLocked"
                    @click="unpauseFromPortal"
                  >
                    {{ t('billing_pause_undo') }}
                  </Button>
                  <!-- Hidden once a cancellation is pending, as the active branch does below —
                       otherwise the page a paused user lands on after cancelling is identical to
                       the one they left, and they cancel again (DAB-922). -->
                  <BillingCouponApplyButton
                    v-if="hasStripeCustomer && canApplyCouponToSubscription"
                    :product-id="currentProductId"
                    :disabled="actionDisabled"
                    link
                    class="billing-portal__coupon"
                  />
                  <!-- Always visible, never a tooltip. Resume is the only route out of a pause,
                       and while it is locked this sentence *is* the next action (wait for the
                       date, or contact support). A tooltip cannot carry that: there is no hover
                       on touch, so mobile users got a dead button and nothing else — and nobody
                       hovers a disabled control anyway. DAB-1327. -->
                  <p v-if="isPauseLocked" class="billing-portal__lock-note">
                    {{ t('billing_pause_lock_tooltip', { date: pauseLockedUntilFormatted }) }}
                  </p>
                  <div v-if="!subscription?.cancel_at_period_end" class="billing-portal__cancel">
                    <Button variant="secondary" finish="link" :disabled="actionDisabled" @click="goToExitSurvey">
                      {{ t('billing_cancel_subscription', 'Cancel Subscription') }}
                    </Button>
                  </div>
                </template>

                <!-- Trialing without a payment method on file -->
                <template v-else-if="isTrialing && !isSubscribedDuringTrial">
                  <Button variant="primary" finish="outlined" :disabled="actionDisabled" @click="goToPlans">
                    {{ t('billing_subscribe_now') }}
                  </Button>
                  <Button
                    variant="danger"
                    finish="link"
                    :disabled="actionDisabled"
                    @click="confirmAndCancelSubscription('now')"
                  >
                    {{ t('billing_cancel_trial') }}
                  </Button>
                </template>

                <!-- Active paid (or trialing with payment method on file) / past due:
                     primary verbs inline, secondary ones behind the ellipsis menu. Cancel is a
                     primary verb here, as it already is in the paused and trialing branches: behind
                     the unlabeled ellipsis it went unfound — the reporter clicked everything visible
                     for 27 minutes, concluded the button was broken, and had support cancel and
                     refund her by hand (DAB-910). -->
                <template v-else-if="isActivePaid || isSubscribedDuringTrial || subscription?.status === 'past_due'">
                  <Button variant="primary" finish="outlined" :disabled="actionDisabled" @click="goToPlans">
                    {{ t('billing_change_plan') }}
                  </Button>
                  <Button
                    v-if="!isYearlyPlan && subscription?.status !== 'past_due'"
                    v-tooltip="t('billing_annual_switch_message')"
                    variant="primary"
                    finish="outlined"
                    :disabled="actionDisabled"
                    @click="switchToAnnual"
                  >
                    {{ t('billing_annual_switch') }}
                  </Button>
                  <!-- Deliberately still a menu, even though it now holds Pause and Apply Coupon —
                       and only Pause when no coupon can be applied. The ellipsis came from the
                       mockup (23b7c24): the row slims to Change Plan + Switch to Annual, with the
                       rest tucked away, and promoting Pause too would put five controls back in it.
                       Whether Pause deserves Cancel's treatment is a design question, not decided
                       here (#225 review) — if it is promoted, drop the Dropdown when it would hold
                       one item rather than leave an unlabeled "…" over a single entry. -->
                  <Dropdown placement="bottom-end">
                    <template #trigger="{ onClick }">
                      <Button
                        icon-only="ellipsis"
                        variant="primary"
                        finish="outlined"
                        :disabled="actionDisabled"
                        :aria-label="t('billing_more_actions')"
                        @click="onClick"
                      />
                    </template>
                    <template #default="{ close }">
                      <Menu>
                        <!-- The object form lets the directive unwire the tooltip if the gate
                             expires while the menu is open; '' only works on mount. -->
                        <MenuItem
                          v-tooltip="{ content: pauseUnavailableMessage ?? '', disabled: !pauseUnavailableReason }"
                          :aria-disabled="pauseUnavailableReason ? 'true' : undefined"
                          @click="
                            close();
                            onPauseClick();
                          "
                        >
                          {{ t('billing_pause_subscription') }}
                        </MenuItem>
                        <MenuItem
                          v-if="canApplyCouponToSubscription"
                          @click="
                            close();
                            couponButton?.open();
                          "
                        >
                          {{ t('billing_coupon_apply') }}
                        </MenuItem>
                      </Menu>
                    </template>
                  </Dropdown>
                  <div v-if="!subscription.cancel_at_period_end" class="billing-portal__cancel">
                    <Button variant="secondary" finish="link" :disabled="actionDisabled" @click="goToExitSurvey">
                      {{ t('billing_cancel_subscription', 'Cancel Subscription') }}
                    </Button>
                  </div>
                  <BillingCouponApplyButton
                    v-if="hasStripeCustomer && canApplyCouponToSubscription"
                    ref="couponButton"
                    :product-id="currentProductId"
                    :trigger="false"
                    :disabled="actionDisabled"
                  />
                </template>

                <!-- Fallback -->
                <template v-else>
                  <Button variant="primary" finish="outlined" :disabled="actionDisabled" @click="goToPlans">
                    {{ t('billing_change_plan') }}
                  </Button>
                  <BillingCouponApplyButton
                    v-if="hasStripeCustomer && canApplyCouponToSubscription"
                    :product-id="currentProductId"
                    :disabled="actionDisabled"
                    link
                    class="billing-portal__coupon"
                  />
                </template>
              </div>
            </div>
          </div>

          <div v-else class="billing-portal__plan">
            <span class="billing-portal__eyebrow">{{ t('billing_plan_current') }}</span>
            <Heading
              :level="3"
              :semantic-level="2"
              family="secondary"
              :margin="false"
              class="billing-portal__plan-name"
              >{{ t('billing_no_active_plan') }}</Heading
            >
            <div class="billing-portal__actions-row">
              <Button
                v-if="trialAvailable"
                variant="primary"
                finish="outlined"
                :disabled="actionDisabled"
                @click="onStartTrialClick"
              >
                {{ t('billing_trial_start') }}
              </Button>
              <Button
                variant="primary"
                finish="outlined"
                :disabled="actionDisabled"
                @click="onSubscribeNowEnsureCustomer"
              >
                {{ t('billing_subscribe_now') }}
              </Button>
              <BillingCouponApplyButton
                v-if="hasStripeCustomer && canApplyCouponToSubscription"
                :product-id="currentProductId"
                :disabled="actionDisabled"
                link
                class="billing-portal__coupon"
              />
            </div>
          </div>
        </div>
      </Block>

      <template v-if="hasStripeCustomer">
        <section class="billing-portal__section">
          <Heading :level="4" :semantic-level="2" :margin="false" class="billing-portal__section-title">{{
            t('billing_payment_method')
          }}</Heading>
          <BillingPaymentMethodList />
        </section>

        <section class="billing-portal__section">
          <Heading :level="4" :semantic-level="2" :margin="false" class="billing-portal__section-title">{{
            t('billing_invoice_history')
          }}</Heading>
          <Prose v-if="isLifetime" variant="secondary" size="small" class="billing-portal__section-hint">
            {{ lifetimeInvoiceNote }}
          </Prose>
          <BillingInvoiceList />
        </section>
      </template>

      <Modal v-model="showPauseModal" size="small" :title="t('billing_pause_subscription')" @close="closePauseModal">
        <BillingPauseSubscriptionForm
          @pause-subscription="handleSubscriptionPaused"
          @back-to-billing="closePauseModal"
        />
      </Modal>

      <Modal v-model="showCancelModal" size="large" :title="t('billing_cancel_subscription', 'Cancel Subscription')">
        <BillingCancelSubscriptionForm
          @cancel="showCancelModal = false"
          @subscription-cancelled="showCancelModal = false"
        />
      </Modal>

      <BillingLifetimeWelcome
        :open="showWelcome"
        :upgrade-name="welcomeUpgrade ? baseName : undefined"
        @close="showWelcome = false"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.billing-portal {
  display: flex;
  flex-direction: column;
  gap: space('medium');
  max-width: 46rem;
  padding-bottom: space('large');

  &__header {
    display: flex;
    flex-direction: column;
    gap: space('xxsmall');
  }

  &__title {
    font-size: fontSize('xlarge');
  }

  &__lede {
    margin: 0;
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: space('small');
  }

  &__eyebrow {
    display: block;
    font-size: fontSize('xsmall');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: theme('color-text-placeholder');
  }

  // The skeleton keeps the eyebrow at the card top while the plan is unknown.
  &__card > &__eyebrow {
    margin-bottom: space('small');
  }

  // Accented to the card's metal, sitting directly above the plan name.
  &__card--gold &__eyebrow {
    color: #b47a02;
  }

  &__card--bronze &__eyebrow {
    color: #8a5a28;
  }

  &__card--silver &__eyebrow {
    color: #5b6675;
  }

  &__name-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: space('xsmall');
  }

  // Dividers only between sections — the plan card above stands on its own.
  &__section + &__section {
    border-top: 1px solid theme('color-border-primary');
    padding-top: space('large');
  }

  &__section-title {
    margin-bottom: space('small');
  }

  &__section-hint {
    margin: calc(-1 * #{space('xxsmall')}) 0 space('small');
    max-width: 56ch;
  }

  // Breathing room between the plan card and the sections below it (the page's
  // flex gap alone reads too tight under the tinted card).
  &__card {
    margin-bottom: space('small');
  }

  // Metal tints keyed to the member seal: a warm wash for bronze/gold, cool for
  // silver — subtle enough that alerts and buttons still sit comfortably on top.
  &__card--gold {
    border: 1px solid #eedfb2;
    background:
      radial-gradient(130% 170% at 0% 0%, rgba(245, 165, 3, 0.14) 0%, transparent 55%),
      radial-gradient(120% 160% at 100% 110%, rgba(74, 217, 217, 0.1) 0%, transparent 55%), #fffdf6;
  }

  &__card--silver {
    border: 1px solid #e2e6ec;
    background: radial-gradient(130% 170% at 0% 0%, rgba(148, 163, 184, 0.16) 0%, transparent 55%), #fcfdfe;
  }

  &__card--bronze {
    border: 1px solid #ecdcc6;
    background: radial-gradient(130% 170% at 0% 0%, rgba(162, 98, 44, 0.12) 0%, transparent 55%), #fdfaf5;
  }

  &__plan-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: space('medium');
  }

  &__seal {
    position: relative;
    flex: 0 0 6.75rem;
    width: 6.75rem;
    filter: drop-shadow(0 6px 14px rgba(90, 74, 40, 0.25));
  }

  // The lifetime seal gets pride of place — well larger than bronze/silver.
  &__seal--gold {
    flex-basis: 9.72rem;
    width: 9.72rem;
  }

  &__spark {
    position: absolute;
    width: 0.875rem;
    height: 0.875rem;
    fill: #f5c34d;
    animation: billing-portal-twinkle 2.8s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__spark--1 {
    top: -0.3rem;
    right: -0.4rem;
  }

  &__spark--2 {
    bottom: 0.25rem;
    left: -0.55rem;
    width: 0.625rem;
    height: 0.625rem;
    animation-delay: 0.9s;
  }

  &__spark--3 {
    top: 2.25rem;
    right: -0.9rem;
    width: 0.5rem;
    height: 0.5rem;
    animation-delay: 1.7s;
  }

  &__congrats {
    margin: 0;
    font-weight: fontWeight('semibold');
  }

  &__congrats-detail {
    margin: 0;
    max-width: 46ch;
  }

  &__perks {
    display: flex;
    flex-wrap: wrap;
    gap: space('xxsmall') space('medium');
    margin: space('xsmall') 0 0;
    padding: 0;
    list-style: none;
  }

  &__perk {
    display: flex;
    align-items: center;
    gap: space('xxsmall');
    font-size: fontSize('small');
    font-weight: fontWeight('medium');
  }

  &__perk-check {
    flex: none;
    width: 1rem;
    height: 1rem;
    color: #b47a02;
  }

  // The mockup's meta band: stats on the left, compact actions flowing right,
  // behind a dashed divider tinted to the card's metal.
  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: space('small') space('large');
    width: 100%;
    margin-top: space('small');
    padding-top: space('xsmall');
    border-top: 1px dashed rgba(91, 102, 117, 0.3);
  }

  &__card--gold &__meta {
    border-top-color: rgba(180, 122, 2, 0.35);
  }

  &__card--bronze &__meta {
    border-top-color: rgba(138, 90, 40, 0.3);
  }

  &__meta-summary {
    width: auto;
  }

  &__meta-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: space('xxsmall') space('xsmall');
    margin-left: auto;
  }

  &__actions-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: space('xsmall');
    margin-top: space('xxsmall');
  }

  &__stat {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__stat-label {
    font-size: fontSize('xxsmall');
    font-weight: fontWeight('bold');
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: theme('color-text-placeholder');
  }

  &__stat-value {
    font-size: fontSize('small');
    font-weight: fontWeight('semibold');
    color: theme('color-text-primary');
  }

  // App-owned, like the sidebar nav buttons: quillui's Button variants don't
  // have this quiet gold-ghost look and the moment deserves it.
  &__replay {
    padding: space('xxsmall') space('xsmall');
    border: none;
    border-radius: borderRadius('small');
    background: transparent;
    color: #b47a02;
    font: inherit;
    font-size: fontSize('xsmall');
    font-weight: fontWeight('semibold');
    cursor: pointer;

    &:hover {
      background: rgba(245, 165, 3, 0.12);
    }

    &:focus-visible {
      outline: 2px solid #b47a02;
      outline-offset: 2px;
    }
  }

  &__plan {
    flex: 1 1 18rem;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: space('xsmall');
  }

  &__plan-name {
    min-width: 0;
  }

  &__plan-desc {
    margin: 0;
  }

  &__summary {
    margin: 0;
    max-width: 46ch;
  }

  // The pause-lock explanation, shown inline rather than in a tooltip. flex-basis pushes it
  // onto its own row below the action buttons in __meta-actions.
  &__lock-note {
    flex-basis: 100%;
    margin: 0;
    font-size: fontSize('xsmall');
    color: theme('color-text-placeholder');
  }

  // Its own row, well clear of the buttons, so a tap meant for them can't land on it.
  &__cancel {
    flex-basis: 100%;
    margin-top: space('small');
  }
}

@media (max-width: 640px) {
  .billing-portal__meta-actions {
    margin-left: 0;
  }

  .billing-portal__seal {
    flex-basis: 5.5rem;
    width: 5.5rem;
  }

  .billing-portal__seal--gold {
    flex-basis: 7.92rem;
    width: 7.92rem;
  }
}

@keyframes billing-portal-twinkle {
  0%,
  100% {
    transform: scale(0.55) rotate(0deg);
    opacity: 0.35;
  }

  50% {
    transform: scale(1) rotate(18deg);
    opacity: 1;
  }
}
</style>
